import { parseWsEvent, type WsEvent } from './events';

export interface WsClientOptions {
  /** Defaults to `/api/v1/ws` on the current origin. */
  path?: string;
  /** Reconnect backoff, ms. Capped, jittered — a server restart must not become a thundering herd. */
  baseDelayMs?: number;
  maxDelayMs?: number;
}

type Listener = (event: WsEvent) => void;

/**
 * `connecting` covers both the first attempt and every reconnect wait — from the operator's
 * point of view "the socket is coming up" and "the socket is coming back up" are the same
 * state, and the indicator should not flap to a scary amber between two retries of a blip.
 * `closed` means we have given up or were never asked to connect.
 */
export type WsStatus = 'connecting' | 'open' | 'closed';

type StatusListener = (status: WsStatus) => void;

const DEFAULT_PATH = '/api/v1/ws';
const DEFAULT_BASE_DELAY = 1000;
const DEFAULT_MAX_DELAY = 30_000;

/**
 * One socket per tab. Auto-reconnects with exponential backoff and jitter, pauses while the tab
 * is hidden is NOT done on purpose — a buyer who tabs away and comes back should already have
 * the fresh cart the other tab pushed.
 */
export class WsClient {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private status: WsStatus = 'closed';
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByUs = false;

  private readonly path: string;
  private readonly baseDelayMs: number;
  private readonly maxDelayMs: number;

  constructor(options: WsClientOptions = {}) {
    this.path = options.path ?? DEFAULT_PATH;
    this.baseDelayMs = options.baseDelayMs ?? DEFAULT_BASE_DELAY;
    this.maxDelayMs = options.maxDelayMs ?? DEFAULT_MAX_DELAY;
  }

  connect(): void {
    if (typeof window === 'undefined') return;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.closedByUs = false;
    this.setStatus('connecting');

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${window.location.host}${this.path}`;

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.attempts = 0;
      this.setStatus('open');
    };

    socket.onmessage = (message: MessageEvent<string>) => {
      const event = parseWsEvent(message.data);
      if (!event) return;
      for (const listener of this.listeners) listener(event);
    };

    socket.onclose = () => {
      this.socket = null;
      if (this.closedByUs) {
        this.setStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // `onclose` always follows; the reconnect is scheduled there so it happens exactly once.
    };
  }

  disconnect(): void {
    this.closedByUs = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
    this.attempts = 0;
    this.setStatus('closed');
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getStatus(): WsStatus {
    return this.status;
  }

  /** Subscribe to connection state. Fires immediately with the current value. */
  subscribeStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  private setStatus(next: WsStatus): void {
    if (this.status === next) return;
    this.status = next;
    for (const listener of this.statusListeners) listener(next);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    // We keep retrying indefinitely rather than giving up: an admin leaves this tab open all day,
    // and a server restart must heal itself without a manual reload. The backoff is what keeps
    // that from being a thundering herd.
    //
    // The status stays `connecting` across the wait, not `closed`. `closed` is reserved for "we
    // are not trying" (disconnected, or never enabled) — showing it during a routine 2 s backoff
    // would flap the top-bar warning on every blip and train the eye to ignore it.
    this.setStatus('connecting');

    // Exponential with ±20 % jitter, mirroring the outbox retry policy's shape.
    const exponential = Math.min(this.baseDelayMs * 2 ** this.attempts, this.maxDelayMs);
    const jitter = exponential * (0.8 + Math.random() * 0.4);
    this.attempts += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, jitter);
  }
}
